import axios, { AxiosRequestConfig, AxiosError } from "axios";
import { Buffer } from "buffer";
import { getCache } from "../utils/PersistenceUtils";
import {
  AUTHENTICATION_DATA_STORAGE_KEY,
  IAuthenticationData,
  setAuthentication
} from "../pages/shared/AuthenticationContext";
import RequestService from "./RequestService";
import { CompanyAuth } from "../entities/login/CompanyAuth";
import { sha256 } from "js-sha256";
import { CompanyToken } from "../entities/login/CompanyToken";
import { UserPayload } from "../entities/login/UserPayload";
import CompanyService from "./CompanyService";
import { onClearCacheSubject } from "../pages/shared/Logout";
import { AccountPasswordExpiredTokenRequest } from "./AccountService";
import ErrorLoggingService from "./ErrorLoggingService";

const REFRESH_BUFFER_MS = 1000 * 10;

export type ValidLogonResponse = {
  response: "OK" | "Ok";
  token: string;
  lastcompanyguid: string;
  twofadata?: string;
};
export type ValidChangePasswordResponse = {
  data: {
    response: string;
    token: string;
    twofadata: string;
    lastcompanyguid: string;
  };
};
export type LogonResponse =
  | {
      response: "2AF";
      twofadata: string;
    }
  | {
      response: "LoginIncorrect";
    }
  | {
      response: "NextBlock";
    }
  | ValidLogonResponse
  | {
      response: "Blocked";
    }
  | {
      response: "passwordexpired";
      token: "";
      twofadata: "";
      lastcompanyguid: "";
      expiredtoken: string;
    }
  | Error;
export type ChangePasswordResponse =
  | {
      data: { response: "invalidtoken" };
    }
  | ValidChangePasswordResponse;

export type ICompanyData = { companyToken: undefined } | CompanyAuth;
export type RefreshRequest = {
  refreshToken: IAuthenticationData;
  refreshlists?: 1;
  filteruseridpaused?: 1 | 0;
};

export type ChangePasswordParamsType = {
  email: string;
  password: string;
  token: string;
};

class AuthenticationService {
  // Mutex para prevenir refreshes simultáneos
  private static refreshTokenPromise: Promise<string | null> | null = null;

  public static async getAuthenticationToken(
    abortController?: AbortController
  ): Promise<string | null> {
    let authenticationData = getCache<IAuthenticationData>(
      AUTHENTICATION_DATA_STORAGE_KEY
    );
    if (!authenticationData) {
      return null;
    }

    // Si el access token está a punto de expirar, refrescar
    if (authenticationData.expires * 1000 - REFRESH_BUFFER_MS < Date.now()) {
      const refreshedToken = await AuthenticationService.tryRefreshToken(
        authenticationData,
        abortController
      );
      if (refreshedToken) {
        return refreshedToken;
      }
      // Si el refresh falló, re-leer authenticationData
      authenticationData = getCache<IAuthenticationData>(
        AUTHENTICATION_DATA_STORAGE_KEY
      );
      if (!authenticationData) {
        return null;
      }
    }

    return authenticationData.token;
  }

  private static async tryRefreshToken(
    data: IAuthenticationData,
    abortController?: AbortController
  ): Promise<string | null> {
    if (
      !data.refreshToken ||
      !data.refreshExpires ||
      data.refreshExpires * 1000 < Date.now()
    ) {
      return null;
    }
    return await this.refreshToken({ refreshToken: data }, abortController);
  }

  public static async refreshToken(
    refreshRequest: RefreshRequest,
    abortController?: AbortController
  ): Promise<string | null> {
    // Si ya hay un refresh en progreso, esperar ese Promise en lugar de iniciar uno nuevo
    if (this.refreshTokenPromise) {
      return await this.refreshTokenPromise;
    }

    // Crear nueva Promise para el refresh
    this.refreshTokenPromise = this.executeRefreshToken(
      refreshRequest,
      abortController
    ).finally(() => {
      // Limpiar la Promise cuando termine (éxito o error)
      this.refreshTokenPromise = null;
    });

    return await this.refreshTokenPromise;
  }

  private static async executeRefreshToken(
    refreshRequest: RefreshRequest,
    abortController?: AbortController
  ): Promise<string | null> {
    const refreshData: string = JSON.stringify({
      ...refreshRequest,
      refreshToken: refreshRequest.refreshToken.refreshToken
    });
    const config: AxiosRequestConfig = {
      method: "post",
      timeout: 30000,
      url: process.env.REACT_APP_INTIZA_AUTH + "/companyauth/refresh",
      headers: { "Content-Type": "application/json" },
      data: refreshData,
      signal: abortController?.signal
    };
    try {
      const refreshResult = await axios(config);
      if (refreshRequest.refreshlists) {
        this.processCompanyLogin(refreshResult.data);
        return refreshResult.data.companyToken;
      } else if (refreshResult.data?.companyToken) {
        const companyToken: string = refreshResult.data.companyToken || "";
        const companyTokenPayload: CompanyToken = parseJwt(companyToken) || {};
        const companyTokenExp: number = companyTokenPayload["exp"] || 0;

        const refreshToken: string = refreshResult.data.refreshToken || "";
        const refreshTokenPayload: any = parseJwt(refreshToken) || {};
        const refreshTokenExp: number = refreshTokenPayload["exp"] || 0;

        CompanyService.setCompanyToken(companyTokenPayload);
        const authData: IAuthenticationData = {
          token: companyToken,
          expires: companyTokenExp,
          refreshToken: refreshToken,
          refreshExpires: refreshTokenExp
        };

        setAuthentication(authData);
        return refreshResult.data.companyToken;
      }
    } catch (error) {
      // Mejorar manejo de errores con logging
      const axiosError = error as AxiosError;
      const errorMessage = axiosError.response?.status
        ? `Refresh token failed with status ${axiosError.response.status}`
        : `Refresh token failed: ${axiosError.message || "Unknown error"}`;

      // Log error para debugging
      const refreshUrl =
        process.env.REACT_APP_INTIZA_AUTH + "/companyauth/refresh";
      await ErrorLoggingService.logRequestError(axiosError, refreshUrl, {
        refreshRequest: { ...refreshRequest, refreshToken: "[REDACTED]" }
      }).catch(() => {
        // Silently fail si el logging falla para evitar loops
        console.warn("Failed to log refresh token error:", errorMessage);
      });

      // Distinguir entre errores temporales y permanentes
      if (axiosError.response?.status === 401) {
        // Token inválido o expirado - error permanente
        console.warn("Refresh token is invalid or expired");
      } else if (
        axiosError.code === "ECONNABORTED" ||
        axiosError.code === "ETIMEDOUT"
      ) {
        // Timeout - error temporal
        console.warn("Refresh token request timed out");
      } else if (axios.isCancel(axiosError)) {
        // Request cancelado - no es un error real
        return null;
      } else {
        // Otro error de red - error temporal
        console.warn("Refresh token request failed:", errorMessage);
      }
    }
    return null;
  }

  public static recoverPassword(email: string): Promise<unknown | Error> {
    return RequestService.post("/auth/recover", { email });
  }

  public static changePasswordExpired(
    request: AccountPasswordExpiredTokenRequest
  ): Promise<any | Error> {
    return RequestService.post(
      "/auth/setpassword",
      request,
      false,
      process.env.REACT_APP_INTIZA_AUTH
    );
  }

  public static async login(
    email: string,
    password: string
  ): Promise<LogonResponse> {
    const userData = await RequestService.post<LogonResponse>(
      "/auth/logon",
      {
        email,
        password: sha256(password)
      },
      false,
      process.env.REACT_APP_INTIZA_AUTH
    );
    if (userData instanceof Error) {
      return new Error("Failed to logon");
    }
    if (userData.response === "OK") {
      AuthenticationService.saveUserPayload(userData);
    }
    return userData;
  }

  public static async login2AFValidation(
    email: string,
    code: string,
    twofadata: string
  ): Promise<LogonResponse> {
    const userData = await RequestService.post<LogonResponse>(
      "/auth/code",
      {
        email,
        code,
        twofadata
      },
      false,
      process.env.REACT_APP_INTIZA_AUTH
    );

    if (userData instanceof Error) {
      return new Error("LoginIncorrect");
    }
    if (userData.response === "LoginIncorrect") {
      return new Error("LoginIncorrect");
    }
    if (userData.response === "OK" || userData.response === "Ok") {
      AuthenticationService.saveUserPayload(userData);
    }
    return userData;
  }

  public static saveUserPayload(userData: ValidLogonResponse) {
    const userToken: string = userData.token;
    const userTokenPayload: UserPayload = parseJwt(userToken);
    CompanyService.setUserPayload(userTokenPayload);
  }

  public static async companyLogin(
    userData: ValidLogonResponse,
    rememberme: boolean
  ): Promise<Error | IAuthenticationData> {
    const companyConfig: AxiosRequestConfig = {
      method: "post",
      timeout: 30000,
      url: process.env.REACT_APP_INTIZA_AUTH + "/companyauth/authorize",
      headers: {
        Authorization: "Bearer " + userData.token,
        "Content-Type": "application/json"
      },
      data: {
        companyguid: userData.lastcompanyguid,
        rememberme
      }
    };
    try {
      const response = await axios(companyConfig);
      return AuthenticationService.processCompanyLogin(response.data);
    } catch (error) {
      return error as Error;
    }
  }

  private static processCompanyLogin(
    loginData: ICompanyData
  ): Error | IAuthenticationData {
    if (!loginData.companyToken) {
      return new Error("No company token");
    }
    const companyToken: string = loginData.companyToken || "";
    const companyTokenPayload: CompanyToken = parseJwt(companyToken) || {};
    const companyTokenExp: number = companyTokenPayload["exp"] || 0;
    // @ts-ignore
    const refreshToken: string = loginData.refreshToken || "";
    const refreshTokenPayload: any = parseJwt(refreshToken) || {};
    const refreshTokenExp: number = refreshTokenPayload["exp"] || 0;
    // @ts-ignore
    CompanyService.setCompanyAuth(loginData);
    CompanyService.setCompanyToken(companyTokenPayload);

    const authData: IAuthenticationData = {
      token: companyToken,
      expires: companyTokenExp,
      refreshToken: refreshToken,
      refreshExpires: refreshTokenExp
    };
    return authData;
  }

  public static async switchCompany(
    to: string
  ): Promise<Error | IAuthenticationData> {
    const result = await RequestService.post<ICompanyData>(
      "/companyauth/switchcompany",
      { companyguid: to },
      true,
      process.env.REACT_APP_INTIZA_AUTH
    );
    if (
      result instanceof Error ||
      result.companyToken === undefined ||
      // @ts-ignore
      result.companyGuid !== to
    ) {
      return new Error("Failed to switch company");
    }
    onClearCacheSubject.next(false);
    return AuthenticationService.processCompanyLogin(result);
  }
}

function parseJwt(token: string): any {
  const base64Payload = token.split(".")[1];
  const payload = Buffer.from(base64Payload, "base64");
  return JSON.parse(payload.toString());
}

export default AuthenticationService;
